'use client'

/**
 * InstallCommand
 * Hero-style pill that detects the visitor's OS and shows the one-line
 * install command, with a platform switcher and copy-to-clipboard button.
 * Fully responsive / mobile-first.
 */

import React, { useEffect, useMemo, useState } from 'react'

const PLATFORMS = [
  {
    id: 'macos',
    label: 'macOS',
    command: 'curl -fsSL https://api.ccmakers.com.br/install.sh | bash',
  },
  {
    id: 'linux',
    label: 'Linux',
    command: 'curl -fsSL https://api.ccmakers.com.br/install.sh | bash',
  },
  {
    id: 'windows',
    label: 'Windows',
    command: 'irm https://api.ccmakers.com.br/install.ps1 | iex',
  },
]

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'macos'
  const ua = (navigator.userAgent || '').toLowerCase()
  const uaDataPlatform = (navigator.userAgentData?.platform || '').toLowerCase()
  const platform = (navigator.platform || '').toLowerCase()
  const candidate = uaDataPlatform || platform || ua

  if (candidate.includes('win')) return 'windows'
  if (candidate.includes('mac') || candidate.includes('darwin') || /iphone|ipad|ipod/.test(ua)) return 'macos'
  if (candidate.includes('linux') || candidate.includes('x11') || candidate.includes('android')) return 'linux'
  return 'macos'
}

export default function InstallCommand() {
  const [selected, setSelected] = useState('macos')
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setSelected(detectPlatform())
  }, [])

  const current = useMemo(
    () => PLATFORMS.find(p => p.id === selected) || PLATFORMS[0],
    [selected]
  )

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(current.command)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  return (
    <>
      {/* Inject mobile styles via <style> tag (no external CSS dependency) */}
      <style>{`
        .ccm-install-pill {
          display: inline-flex;
          align-items: stretch;
          flex-direction: row;
          background: rgba(10,14,18,0.92);
          border: 1px solid rgba(0,255,65,0.28);
          border-radius: 12px;
          overflow: visible;
          max-width: 100%;
          box-shadow: 0 4px 24px rgba(0,0,0,0.35);
          font-family: var(--font-mono, monospace);
          position: relative;
          line-height: 1;
        }
        .ccm-install-selector {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: transparent;
          color: var(--sc-text-primary, #fff);
          border: none;
          border-right: 1px solid rgba(0,255,65,0.18);
          border-radius: 12px 0 0 12px;
          cursor: pointer;
          font-family: var(--font-terminal, monospace);
          font-size: 0.8125rem;
          white-space: nowrap;
          line-height: 1;
        }
        .ccm-install-cmd {
          display: inline-flex;
          align-items: center;
          padding: 0.75rem 0.75rem;
          color: var(--sc-text-primary, #fff);
          font-size: 0.8125rem;
          overflow-x: auto;
          white-space: nowrap;
          flex: 1 1 auto;
          min-width: 0;
          line-height: 1;
          scrollbar-width: none;
        }
        .ccm-install-cmd::-webkit-scrollbar { display: none; }
        .ccm-install-cmd code {
          font-family: var(--font-mono, monospace);
          color: rgba(255,255,255,0.92);
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          line-height: 1;
          white-space: nowrap;
          font-size: inherit;
        }
        .ccm-install-copy {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.75rem;
          background: transparent;
          border: none;
          border-left: 1px solid rgba(0,255,65,0.18);
          border-radius: 0 12px 12px 0;
          cursor: pointer;
          font-family: var(--font-terminal, monospace);
          font-size: 0.75rem;
          white-space: nowrap;
          line-height: 1;
          gap: 4px;
        }
        .ccm-install-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          min-width: 100%;
          margin: 0;
          padding: 4px;
          list-style: none;
          background: rgba(10,14,18,0.98);
          border: 1px solid rgba(0,255,65,0.25);
          border-radius: 10px;
          z-index: 40;
          box-shadow: 0 8px 24px rgba(0,0,0,0.5);
        }
        .ccm-install-dropdown button {
          width: 100%;
          text-align: left;
          padding: 0.5rem 0.75rem;
          background: transparent;
          color: var(--sc-text-primary, #fff);
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-family: var(--font-terminal, monospace);
          font-size: 0.8125rem;
        }
        .ccm-install-dropdown button:hover,
        .ccm-install-dropdown button[aria-selected="true"] {
          background: rgba(0,255,65,0.08);
        }

        /* ── Mobile-first: stack vertically on small screens ── */
        @media (max-width: 600px) {
          .ccm-install-pill {
            flex-direction: column;
            border-radius: 10px;
            width: 100%;
          }
          .ccm-install-selector {
            border-right: none;
            border-bottom: 1px solid rgba(0,255,65,0.18);
            border-radius: 10px 10px 0 0;
            justify-content: center;
            padding: 0.625rem 0.75rem;
            font-size: 0.75rem;
          }
          .ccm-install-cmd {
            padding: 0.625rem 0.75rem;
            font-size: 0.6875rem;
          }
          .ccm-install-copy {
            border-left: none;
            border-top: 1px solid rgba(0,255,65,0.18);
            border-radius: 0 0 10px 10px;
            padding: 0.5rem 0.75rem;
            justify-content: center;
          }
          .ccm-install-dropdown {
            left: 0; right: 0;
          }
        }
      `}</style>

      <div
        className="ccm-install-pill"
        onMouseLeave={() => setOpen(false)}
      >
        {/* Platform selector */}
        <div style={{ position: 'relative', display: 'flex' }}>
          <button
            type="button"
            className="ccm-install-selector"
            onClick={() => setOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            Get CCMakers Code · {current.label}
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ display: 'block' }}>
              <path d="M2 4 L6 8 L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {open && (
            <ul role="listbox" className="ccm-install-dropdown">
              {PLATFORMS.map(p => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={p.id === selected}
                    onClick={() => { setSelected(p.id); setOpen(false) }}
                  >
                    {p.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Command text */}
        <div className="ccm-install-cmd">
          <span style={{ color: 'var(--sc-green-bright, #00ff41)', marginRight: '0.5rem', lineHeight: 1, flexShrink: 0 }}>$</span>
          <code>{current.command}</code>
        </div>

        {/* Copy button */}
        <button
          type="button"
          className="ccm-install-copy"
          onClick={copy}
          aria-label="Copiar comando"
          title={copied ? 'Copiado!' : 'Copiar'}
          style={{
            color: copied ? 'var(--sc-green-bright, #00ff41)' : 'var(--sc-text-primary, #fff)',
          }}
        >
          {copied ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              copiado
            </>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
        </button>
      </div>
    </>
  )
}
